<template>
  <div>
    <div class="emd-container">
      <div class="emd-container__header eu-py_4_md eu-px_5_md">
        <h2 class="emd-text _size_section _color_default">
          {{ $t('title') }}
        </h2>
      </div>
      <hr class="emd-divider">
      <div class="emd-container__content eu-pa_5_md">
        <emd-alert
          v-if="tabbarError.status"
          v-once
          variant="danger"
          :title="$t('tabbarError')"
          @cancel="tabbarError.status = false"
          autoclose />
        <div class="el-chunk">
          <div class="eu-mb_4">
            <p class="emd-text _type_caption eu-mb_2">
              {{ $t('active.title') }}
            </p>
            <p class="emd-input-feedback">{{ $t('active.subtitle') }}</p>
          </div>
          <div class="eu-flex">
            <div class="eu-flex-item eu-basis_12 eu-basis_2_md eu-mb_3 eu-no-margin_md" v-for="(item, index) in checkoutExpirationOptions" :key="index">
              <div class="eu-height_100 _border_base emd-form-box" :class="{ 'eu-border-color_focus': item.value === checkout.expires_in }">
                <label class="emd-radio-button eu-flex eu-px_3 eu-py_2" :for="item.id">
                  <input class="emd-radio-button__input" name="expires_in" type="radio" :id="item.id" :value="item.value" v-model="checkout.expires_in">
                  <div class="emd-radio-button__area"></div>
                  <span class="emd-radio-button__text">{{ item.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="el-chunk">
          <div class="el-chunk">
            <p class="emd-text _type_caption">
              {{ $t('acceptedPaymentMethods') }}
            </p>
          </div>
          <div class="eu-flex checkout-payment-type">
            <div class="checkout-payment-type-items" v-if="creditCardEnabled">
              <div
                class="eu-height_100 emd-form-box _border_base _active checkout-payment-type-container"
                :class="{ 'eu-border-color_focus': acceptCreditCard }">
                <div class="emd-form-box__header">
                  <label class="emd-checkbox" for="credit-card">
                    <input
                      class="emd-checkbox__input"
                      name="credit-card"
                      type="checkbox"
                      id="credit-card"
                      v-model="acceptCreditCard"
                      :checked="acceptCreditCard"
                      @click="updatePaymentMethod('credit_card')">
                    <div class="emd-checkbox__area"></div>
                    <i class="emd-form-box__image uil uil-icon-2x uil-credit-card" :class="{ 'emd-text _color_tertiary': acceptCreditCard }"></i>
                    <span class="emd-checkbox__text">
                      {{ $t('creditCard') }}
                    </span>
                  </label>
                </div>
                <div class="emd-form-box__content">
                  <div class="eu-flex" style='flex-direction: column'>
                    <div class="emd-form-group eu-flex-item">
                      <label class="emd-label">{{ $t('installment') }}</label>
                      <div class="emd-select">
                        <select
                          id="installments"
                          name="installments"
                          v-model="checkout.credit_card.installments"
                          :disabled="!acceptCreditCard" >
                          <option v-for="index in 12" :key="index" :value="index">{{ `${index}x ${$t('of')} ${setInstallment(index)}` }}</option>
                        </select>
                        <div class="emd-select__arrow"></div>
                      </div>
                      <p class="emd-input-feedback">{{ $t('hint.installments') }}</p>
                    </div>
                    <statement-descriptor
                      :checkout='checkout'
                      :accept-credit-card='acceptCreditCard'
                      :statementDescriptorFeedbackError=statementDescriptorFeedbackError
                      @setStatementDescriptorFeedbackError=setStatementDescriptorFeedbackError
                    />
                    <div class="emd-form-group eu-flex-item">
                      <label class="emd-label _required" style="display: initial">{{ $t('operation.label') }}</label>
                      <emd-tippy data-template="info" id="info" class="eu-ml_1" :type="'html'" position="bottom">
                        <i class="uil uil-info-circle emd-text _color_subtle" />
                        <emd-tippy-template-HTML id="info">
                          <div style="text-transform: initial; letter-spacing: 0;">
                            <p>
                              {{ $t('operation.info.label') }}
                              <a class="emd-link" :href="urlAuthOnly" target="_blank" rel="noopener noreferrer">{{ $t('operation.info.link') }}</a>
                            </p>
                          </div>
                        </emd-tippy-template-HTML>
                      </emd-tippy>
                      <label class="emd-radio-button eu-flex eu-px_3 eu-py_2">
                        <input class="emd-radio-button__input" type="radio" name="operation_radio" value="auth_and_capture"
                          v-model="checkout.credit_card.operation_type" :disabled="!acceptCreditCard"/>
                        <div class="emd-radio-button__area"></div>
                        <span class="emd-radio-button__text">{{ $t('operation.radio.authorize_capture') }}</span>
                      </label>
                      <label class="emd-radio-button eu-flex eu-px_3 eu-py_2">
                        <input class="emd-radio-button__input" type="radio" name="operation_radio" value="auth_only"
                          v-model="checkout.credit_card.operation_type" :disabled="!acceptCreditCard"/>
                        <div class="emd-radio-button__area"></div>
                        <span class="emd-radio-button__text">{{ $t('operation.radio.authorize_only') }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="checkout-payment-type-items" v-if="boletoEnabled">
              <div
                :class="{ 'eu-border-color_focus': checkout.accepted_payment_methods.includes('boleto') }"
                class="eu-height_100 emd-form-box _border_base _active checkout-payment-type-container">
                <div class="emd-form-box__header">
                  <label class="emd-checkbox" for="boleto">
                    <input
                      class="emd-checkbox__input"
                      name="boleto"
                      type="checkbox"
                      id="boleto"
                      v-model="acceptBoleto"
                      :checked="acceptBoleto"
                      @click="updatePaymentMethod('boleto')">
                    <div class="emd-checkbox__area"></div>
                    <i class="emd-form-box__image uil uil-icon-2x uil-invoice" :class="{ 'emd-text _color_tertiary': checkout.accepted_payment_methods.includes('boleto') }"></i>
                    <span class="emd-checkbox__text">
                        {{ $t('boleto') }}
                    </span>
                  </label>
                </div>
                <div class="emd-form-box__content">
                  <div class="emd-form-group">
                    <label class="emd-label">{{ $t('dueDate') }}</label>
                    <div class="emd-input-group">
                      <input
                        class="emd-input"
                        type="text"
                        v-model="boletoDueAt"
                        v-mask="'##/##/####'"
                        placeholder="dd/mm/aaaa"
                        :disabled="!acceptBoleto"
                        :class="{ '_state_error': validateBoleto }" />
                    </div>
                    <p class="emd-input-feedback">{{ $t('hint.expires_in', { date: boletoExpirationDate }) }}</p>
                    <p class="emd-input-feedback _state_error" v-if="validateBoleto">{{ $t('hint.date') }}</p>
                  </div>
                </div>
              </div>
            </div>
            <pix-card
              :checkout='checkout'
              :checkoutExpirationDate='this.checkout.expires_in'
              :updatePaymentMethod='updatePaymentMethod'
              :paymentMethodsEnabled='paymentMethodsEnabled'
              :pixFeedbackError='pixFeedbackError'
              @setPixFeedbackError='setPixFeedbackError'
            />
          </div>
        </div>
      </div>
    </div>
    <div class="el-section">
      <div class="eu-display_flex eu-justify_space-between eu-wrap_reverse">
        <button class="emd-btn _color_secondary _type_text _block_mobile" @click="reverseRoute">
          <i class="uil uil-angle-left"></i>
          {{ $t('customer') }}
        </button>
        <button class="emd-btn _color_secondary _block_mobile eu-mb_5 eu-no-margin_sm" :disabled="selectedOptions" @click="nextRoute">
          {{ $t('review') }}
          <i class="uil uil-angle-right"></i>
        </button>
      </div>
   </div>
  </div>
</template>

<script>
import EmdAlert from '@/components/emerald/molecules/EmdAlert'
import EmdTippy from '@/components/emerald/atoms/EmdTippy'
import EmdTippyTemplateHTML from '@/components/emerald/atoms/EmdTippyTemplateHTML'
import { requiredIf } from 'vuelidate/lib/validators'
import { validateDate, setCurrentDate, fromatParseDate, validateDateFormat, formatCurrency, isEmpty, formatToInt } from '@/utils/validations'
import StatementDescriptor from '@/components/project/StatementDescriptor.vue'
import PixCard from '@/components/project/paymentLink/PixCard.vue'

export default {
  name: 'PaymentLink',
  components: {
    EmdAlert,
    EmdTippy,
    EmdTippyTemplateHTML,
    StatementDescriptor,
    PixCard
  },
  data () {
    return {
      acceptCreditCard: false,
      acceptBoleto: false,
      dueDate: '',
      boletoDueAt: '',
      pixFeedbackError: false,
      statementDescriptorFeedbackError: false
    }
  },
  computed: {
    checkout () {
      return this.$store.getters.checkout
    },
    tabbarError () {
      return this.$store.getters.tabbarError
    },
    showFeedbacks () {
      return this.$store.getters.showFeedbacks
    },
    selectedPaymentType () {
      return this.$store.getters.selectedPaymentType
    },
    totalAmount () {
      return this.$store.getters.total.amount
    },
    paymentMethodsEnabled () {
      return this.$store.getters.paymentMethodsEnabled
    },
    boletoEnabled () {
      return this.paymentMethodsEnabled.includes('boleto')
    },
    creditCardEnabled () {
      return this.paymentMethodsEnabled.includes('credit_card')
    },
    boletoExpirationDate () {
      return setCurrentDate(this.checkout.expires_in / 60 / 24)
    },
    checkoutExpirationOptions () {
      return this.$store.getters.checkoutExpirationOptions
    },
    validateBoleto () {
      return this.validateBoletoDueAt() && this.showFeedbacks.boleto && this.acceptBoleto
    },
    formIsValid () {
      if (this.acceptBoleto) return !this.validateBoletoDueAt() && !this.selectedOptions
      return !this.selectedOptions
    },
    selectedOptions () {
      return !this.checkout.accepted_payment_methods.length || isEmpty(this.checkout.expires_in)
    },
    urlAuthOnly () {
      return `${process.env.VUE_APP_URL_AUTH_ONLY}`
    }
  },
  validations () {
    return {
      boletoDueAt: {
        required: requiredIf(function () { return this.acceptBoleto }),
        validateDate
      }
    }
  },
  methods: {
    setPixFeedbackError (pixFeedbackErrorEvent) {
      this.pixFeedbackError = pixFeedbackErrorEvent
    },
    setStatementDescriptorFeedbackError (statementFeedbackErrorEvent) {
      this.statementDescriptorFeedbackError = statementFeedbackErrorEvent
    },
    validatePix () {
      if (isEmpty(this.checkout.pix.expires_at)) {
        this.pixFeedbackError = true
      }
      if (this.pixFeedbackError) {
        return false
      } else {
        this.validateForm()
      }
    },
    validateForm () {
      if (this.statementDescriptorFeedbackError) return false
      if (!this.formIsValid) {
        this.showFeedbacks.status = true
        if (this.acceptCreditCard) this.showFeedbacks.credit_card = true
        if (this.acceptBoleto) this.showFeedbacks.boleto = true
      } else this.$router.push({ name: 'ReviewOrder' })
    },
    updatePaymentMethod (method) {
      if (!this.checkout.accepted_payment_methods.includes(method)) {
        this.checkout.accepted_payment_methods.push(method)
      } else {
        this.checkout.accepted_payment_methods.splice(this.checkout.accepted_payment_methods.indexOf(method), 1)
      }
    },
    validateBoletoDueAt () {
      if (this.checkout.boleto.due_at.length === 10 && validateDateFormat(this.checkout.boleto.due_at)) {
        const checkoutDate = fromatParseDate(this.boletoExpirationDate)
        const boletoDate = fromatParseDate(this.checkout.boleto.due_at)
        return checkoutDate >= boletoDate
      } return true
    },
    setInstallment (value) {
      return formatCurrency((formatToInt(this.totalAmount) / value) / 100)
    },
    nextRoute () {
      this.checkout.accepted_payment_methods.includes('pix') ? this.validatePix() : this.validateForm()
    },
    reverseRoute () {
      this.$router.push({ name: 'Customer' })
    }
  },
  beforeMount () {
    if (this.checkout.accepted_payment_methods.includes('boleto')) {
      this.acceptBoleto = true
      this.boletoDueAt = this.checkout.boleto.due_at
    }
    if (this.checkout.accepted_payment_methods.includes('credit_card')) this.acceptCreditCard = true
  },
  watch: {
    acceptBoleto: {
      handler: function () {
        if (!this.checkout.accepted_payment_methods.includes('boleto')) this.boletoDueAt = ''
      }
    },
    acceptCreditCard: {
      handler: function () {
        if (!this.checkout.accepted_payment_methods.includes('credit_card')) {
          this.checkout.credit_card.installments = '1'
          this.checkout.credit_card.operation_type = 'auth_and_capture'
          this.checkout.credit_card.capture = true
        }
      }
    },
    boletoDueAt: {
      handler: function () {
        this.checkout.boleto.due_at = this.boletoDueAt
      }
    },
    formIsValid: {
      handler: function () {
        this.$store.dispatch('SET_PERMISSION', {
          status: !this.formIsValid,
          component: this.$route.name,
          paymentType: this.selectedPaymentType
        })
        this.tabbarError.status = false
        this.showFeedbacks.status = false
      }
    }
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "title": "Configure o link de pagamento",
      "acceptedPaymentMethods": "Quais formas de pagamento serão permitidas?",
      "creditCard": "Cartão de Crédito",
      "installment": "Máximo de parcelas",
      "boleto": "Boleto",
      "dueDate": "Validade",
      "of": "de",
      "active": {
        "title": "Por quanto tempo o link deve estar ativo?",
        "subtitle": "Defina o tempo de expiração, após o periodo não será possível acessar o checkout gerado.",
        "hint": "Defina o tempo de expiração em dias"
      },
      "operation": {
        "label": "Operação",
        "radio": {
          "authorize_capture": "Autorizar e capturar",
          "authorize_only": "Autorizar somente"
        },
        "info": {
          "label": "Para saber mais sobre tipos de operações acesse nosso artigo na",
          "link": "Central de Ajuda"
        }
      },
      "expiration": "Vencimento",
      "customer": "Dados do Cliente",
      "review": "Resumo",
      "hint": {
        "required": "Esse campo é obrigatório.",
        "installments": "Até 12 parcelas",
        "expires_in": "Deve ser maior que a expiração do link ({date})",
        "date": "Data deve ser válida",
        "statement_descriptor": "Até 13 caracteres",
        "length": "Este campo deve conter até {length} caracteres"
      },
      "tabbarError": "Alguns campos não foram preenchidos corretamente, por favor verifique."
    },
    "en-US": {
      "title": "Setup the payment link",
      "acceptedPaymentMethods": "What payment methods will be allowed?",
      "creditCard": "Credit Card",
      "installment": "Max installments",
      "boleto": "Boleto",
      "dueDate": "Due date",
      "of": "of",
      "active": {
        "title": "How long must this payment link be available?",
        "subtitle": "Set the expiration date of your checkout link, after the expiration will not be possible to access the checkout",
        "hint": "Set the expiration time in days"
      },
      "operation": {
        "label": "Operação",
        "radio": {
          "authorize_capture": "Authorize and capture",
          "authorize_only": "Authorize only"
        },
        "info": {
          "label": "To learn more about types of operations,",
          "link": "visit our article at"
        }
      },
      "expiration": "Expiration",
      "customer": "Customer",
      "review": "Review",
       "hint": {
        "required": "This field is required",
        "installments": "until 12 installments",
        "expires_in": "Must be greater than expiration date ({date})",
        "date": "Date must be valid"
      },
      "tabbarError": "Some fields were filled incorrectly, please verify"
    }
  }
</i18n>

<style scoped>
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }

  .uil-icon-2x {
    font-size: 22px;
  }

  .checkout-payment-type {
    justify-content: center;
  }

  .checkout-payment-type-items {
    margin: 1rem;
  }

  .checkout-payment-type-container {
    width: 17rem;
  }

  @media (max-width: 670px) {
    .checkout-payment-type-container {
      width: 20rem;
    }
  }
</style>
